import React, { useState, useEffect } from 'react';
import BlogList from '../components/blog/BlogList';
import BlogSearch from '../components/blog/BlogSearch';
import { useBlog } from '../hooks/useBlog';

const Blog = () => {
  const { posts, loading, error, fetchPublished } = useBlog();
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [searchCategory, setSearchCategory] = useState('All');

  useEffect(() => {
    setFilteredPosts(posts);
  }, [posts]);

  const handleSearch = (query) => {
    const filtered = posts.filter((p) => {
      const matchesQuery = p.title.toLowerCase().includes(query.toLowerCase()) ||
        (p.excerpt || '').toLowerCase().includes(query.toLowerCase());
      const matchesCategory = searchCategory === 'All' || p.category === searchCategory;
      return matchesQuery && matchesCategory;
    });
    setFilteredPosts(filtered);
  };

  const handleCategoryFilter = (category) => {
    setSearchCategory(category);
    const filtered = category === 'All'
      ? posts
      : posts.filter((p) => p.category === category);
    setFilteredPosts(filtered);
  };

  const handleOpenPost = (post) => {
    window.location.href = `/blog/${post.slug}`;
  };

  const categories = ['All', 'Family Law', 'Criminal Law', 'Civil Law', 'Corporate Law'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-5xl font-bold mb-4">Legal Blog & Insights</h1>
          <p className="text-lg text-blue-100 max-w-2xl">
            Stay informed with our latest articles on family law, criminal defense, civil matters, and more. Expert insights to help you understand your legal rights.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Search & Filters */}
        <div className="mb-12">
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Search Articles</h2>
            <BlogSearch onSearch={handleSearch} />
          </div>

          {/* Category Filter */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Categories</h3>
            <div className="flex flex-wrap gap-3">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleCategoryFilter(cat)}
                  className={`px-4 py-2 rounded-full font-semibold transition ${
                    searchCategory === cat
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Articles Grid */}
        <div>
          {loading ? (
            <div className="text-center py-12">
              <p className="text-lg text-gray-600">Loading articles...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-lg text-red-600">Failed to load articles.</p>
              <button onClick={fetchPublished} className="mt-4 bg-blue-600 text-white px-4 py-2 rounded">Retry</button>
            </div>
          ) : filteredPosts.length > 0 ? (
            <>
              <h2 className="text-2xl font-bold text-gray-800 mb-8">
                {searchCategory === 'All' ? 'All Articles' : `${searchCategory} Articles`}
              </h2>
              <BlogList posts={filteredPosts} onOpen={handleOpenPost} />
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-lg text-gray-600">No articles found. Try a different search or category.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Blog;
